import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, ExternalLink, Play, Share2, Scissors, Video } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

const CorteCerto = () => {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    toast.success("Link copiado para a área de transferência!");
    setTimeout(() => setCopied(null), 2000);
  };

  const videos = [
    {
      titulo: "O que aconteceu antes da criação do mundo? Jesus explicou #memesgospel",
      link: "https://rumble.com/v6u23mj-o-que-aconteceu-antes-da-criao-do-mundo-jesus-explicou-memesgospel.html",
      thumbnail: "https://sp.rmbl.ws/s8/6/g/h/5/x/gh5xg.OvCc.1.jpg",
      descricao: "Uma reflexão sobre o que existia antes da criação do mundo, segundo as palavras de Jesus."
    },
    {
      titulo: "NOS TRILHOS DO SENHOR COMPLETAO.mp4",
      link: "https://rumble.com/v6sl2ud-nos-trilhos-do-senhor-completao.mp4.html?e9s=src_v1_ucp",
      thumbnail: "https://sp.rmbl.ws/s8/6/S/U/q/i/SUqii.OvCc.jpg",
      descricao: "Uma jornada completa nos caminhos do Senhor."
    },
    {
      titulo: "Melhores Momentos - Pregação",
      link: "https://rumble.com/c/c-7706553",
      thumbnail: "https://sp.rmbl.ws/s8/6/g/h/5/x/gh5xg.OvCc.1.jpg",
      descricao: "Os melhores momentos da pregação dominical."
    },
    {
      titulo: "Reflexão sobre Provérbios",
      link: "https://rumble.com/c/c-7706553",
      thumbnail: "https://sp.rmbl.ws/s8/6/S/U/q/i/SUqii.OvCc.jpg",
      descricao: "Cortes especiais sobre o livro de Provérbios."
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Corte Certo
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Os melhores cortes e momentos selecionados para você. Mensagens poderosas em formato compacto.
          </p>
        </div>

        <div className="mb-12">
          <Card className="overflow-hidden border border-border">
            <CardHeader className="bg-muted/50">
              <CardTitle className="text-xl flex items-center">
                <Scissors className="mr-2 h-5 w-5 text-primary" /> Canal Corte Certo no Rumble
              </CardTitle>
              <CardDescription>
                Acompanhe os melhores cortes e momentos selecionados
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <p className="text-muted-foreground">
                  Nosso canal Corte Certo no Rumble traz os melhores momentos e recortes de pregações, testemunhos e conteúdos inspiradores. Uma forma rápida e prática de acessar mensagens poderosas sem precisar assistir vídeos longos.
                </p>
                <div className="flex flex-wrap gap-4 items-center justify-between">
                  <Button 
                    asChild 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <a 
                      href="https://rumble.com/c/c-7706553" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Video className="mr-2 h-5 w-5" /> Visitar Canal Corte Certo
                    </a>
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handleCopy("https://rumble.com/c/c-7706553")}
                    className={copied === "https://rumble.com/c/c-7706553" ? "bg-primary/10" : ""}
                  >
                    <Copy size={16} className="mr-1" /> 
                    {copied === "https://rumble.com/c/c-7706553" ? "Copiado!" : "Copiar Link"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Vídeo em Destaque</h2>
          <Card className="overflow-hidden border border-border">
            <div className="aspect-video overflow-hidden bg-muted">
              <img 
                src="https://sp.rmbl.ws/s8/6/g/h/5/x/gh5xg.OvCc.1.jpg" 
                alt="O que aconteceu antes da criação do mundo? Jesus explicou"
                className="w-full h-full object-cover"
              />
            </div>
            <CardHeader>
              <CardTitle>O que aconteceu antes da criação do mundo? Jesus explicou #memesgospel</CardTitle>
              <CardDescription>
                Uma reflexão sobre o que existia antes da criação do mundo, segundo as palavras de Jesus.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Este vídeo aborda uma das questões mais profundas da existência: o que havia antes da criação do mundo? A partir das palavras de Jesus, exploramos esta fascinante questão teológica de forma acessível e inspiradora.
              </p>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <a 
                  href="https://rumble.com/v6u23mj-o-que-aconteceu-antes-da-criao-do-mundo-jesus-explicou-memesgospel.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Play size={16} className="mr-1" /> Assistir Agora
                </a>
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handleCopy("https://rumble.com/v6u23mj-o-que-aconteceu-antes-da-criao-do-mundo-jesus-explicou-memesgospel.html")}
                className={copied === "https://rumble.com/v6u23mj-o-que-aconteceu-antes-da-criao-do-mundo-jesus-explicou-memesgospel.html" ? "bg-primary/10" : ""}
              >
                <Share2 size={16} className="mr-1" /> 
                {copied === "https://rumble.com/v6u23mj-o-que-aconteceu-antes-da-criao-do-mundo-jesus-explicou-memesgospel.html" ? "Copiado!" : "Compartilhar"}
              </Button>
            </CardFooter>
          </Card>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Cortes Recentes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {videos.map((video, index) => (
              <Card key={index} className="overflow-hidden border border-border h-full flex flex-col">
                <div className="aspect-video overflow-hidden bg-muted">
                  <img 
                    src={video.thumbnail} 
                    alt={video.titulo}
                    className="w-full h-full object-cover transition-transform hover:scale-105"
                  />
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">{video.titulo}</CardTitle>
                  <CardDescription>{video.descricao}</CardDescription>
                </CardHeader>
                <CardFooter className="flex justify-between mt-auto">
                  <Button 
                    asChild 
                    variant="default"
                    size="sm"
                  >
                    <a 
                      href={video.link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                    >
                      <Play size={16} className="mr-1" /> Assistir
                    </a>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleCopy(video.link)}
                    className={copied === video.link ? "bg-primary/10" : ""}
                  >
                    <Share2 size={16} className="mr-1" /> 
                    {copied === video.link ? "Copiado!" : "Compartilhar"}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>

        <div className="bg-muted p-6 rounded-lg mb-12">
          <h2 className="text-2xl font-semibold mb-4">Por que assistir nossos cortes?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card p-4 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Economia de Tempo</h3>
              <p className="text-sm text-muted-foreground">Acesse as partes mais importantes e impactantes das mensagens sem precisar assistir vídeos longos.</p>
            </div>
            <div className="bg-card p-4 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Conteúdo Selecionado</h3>
              <p className="text-sm text-muted-foreground">Apenas os melhores momentos e as mensagens mais poderosas são selecionados para nossos cortes.</p>
            </div>
            <div className="bg-card p-4 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Compartilhamento Fácil</h3>
              <p className="text-sm text-muted-foreground">Vídeos curtos são perfeitos para compartilhar com amigos e familiares nas redes sociais.</p>
            </div>
          </div>
        </div>

        <div className="mb-12">
          <Card className="overflow-hidden border border-border">
            <CardHeader className="bg-muted/50">
              <CardTitle className="text-xl">Como são feitos nossos cortes?</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <p className="text-muted-foreground">
                  Nossos cortes são cuidadosamente selecionados e editados para trazer o melhor conteúdo em formato compacto. Utilizamos técnicas profissionais de edição para garantir que a mensagem seja transmitida com clareza e impacto.
                </p>
                <ol className="list-decimal pl-5 space-y-2 text-muted-foreground">
                  <li>Selecionamos os melhores momentos de pregações e testemunhos</li>
                  <li>Realizamos cortes precisos para manter a essência da mensagem</li>
                  <li>Adicionamos legendas quando necessário para melhor compreensão</li>
                  <li>Otimizamos o áudio para uma experiência de qualidade</li>
                  <li>Disponibilizamos em formato curto e acessível para todas as plataformas</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="text-center">
          <Button 
            asChild 
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <a 
              href="https://rumble.com/c/c-7706553" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Inscreva-se no Canal Corte Certo
            </a>
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CorteCerto;